// The four conditions the Connections page and the overview banner keep apart (app-baseline
// 05-client §5.4: not configured is not failing; permission model: not permitted is not empty).
// Pure; unit-tested under node.

import type { ConnectionRunsSummary, ConnectionState, FellowConnectionDto, FellowConnectionResponse, MeResponse } from '../../../shared/api-types.js';
import { FELLOW_STATE_READ_CODE_LABELS, type FellowStateReadCode } from '../../../shared/enums.js';
import { formatDateOnly, pluralise } from '../../ui/format.js';
import type { IconName } from '../../ui/Icon.js';
import { lastRunStatePresentation, type LastRunPresentation } from './connection-runs.js';

export type ConnectionView = 'not-configured' | 'not-permitted' | 'not-connected' | 'connected';

/** Which screen the Connections page shows for a GET /api/connections/fellow answer. */
export function connectionView(response: FellowConnectionResponse): ConnectionView {
  if (response.floor === 'not_configured') return 'not-configured';
  const live = response.connection !== null && response.connection.status !== 'revoked';
  if (live) return 'connected';
  return response.allowed ? 'not-connected' : 'not-permitted';
}

/**
 * The credential's fingerprint as the panels show it, `····0a1b2c3d` (06-modules §6.4 Credential): an identifier of the
 * key or token, computed from it and not part of it — no character of the secret itself is ever shown (the Fellow key's
 * last four left the row, the DTO and the screen in migration 0048).
 */
export function maskedKey(fingerprint: string): string {
  return `····${fingerprint}`;
}

// --- The credential Fellow no longer accepts (05-content §6 Revoked; 06-modules §6.4 Credential) ----------------------

/** The closed code a sync, a tick-off run or a read leaves when Fellow answered 401 to the stored key (`fellow/types.ts`). */
export const FELLOW_KEY_REFUSED = 'fellow_auth_failed';

/**
 * Whether Fellow no longer accepts the stored API key: a sync, a tick-off run or a read that Fellow answered 401 leaves
 * the connection `error` with the code `fellow_auth_failed` (`fellow/types.ts`), and a `revoked` connection — which no
 * server code writes for Fellow today — would say the same. Why a state of its own (05-content §6: "A credential the
 * source no longer accepts → Revoked (Not: Disconnected, Invalid, Expired, Broken) … the reader's next act is to
 * reconnect, not to retry"; the Phase 1c audit's R12): the run that met it failed, but retrying it never helps.
 */
export function fellowCredentialRevoked(connection: Pick<FellowConnectionDto, 'status' | 'lastError'> | null): boolean {
  if (connection === null) return false;
  return connection.status === 'revoked' || (connection.status === 'error' && connection.lastError?.code === FELLOW_KEY_REFUSED);
}

/**
 * The words a revoked Fellow key is said in — the state's word first, the next act beside it, as Slack's panel says its
 * own ("Revoked — Slack no longer accepts Calis's access"). One place, so the banner, the State and Credential rows, the
 * account menu and the Overview's notice never drift apart.
 */
export const FELLOW_REVOKED = {
  /** The state's word, on the State and Credential rows' pills. */
  word: 'Revoked',
  /** Beside the pill: the next act, as a sentence of its own. */
  nextStep: 'Connect again with a new key.',
  bannerTitle: 'Revoked — Fellow no longer accepts your API key',
  detail: 'Fellow no longer accepts the stored API key. Syncing again will not help; connect again with a new key.',
  accountLine: 'Fellow: Revoked — reconnect',
  /** The Connections State row, after the next act: the run that met the refusal, its time following (`fellow-panel.tsx`). */
  stateRunNote: 'Fellow refused the key at the sync',
  /** The Overview's notice (the owner's ruling of 2026-10-09, "Both say Revoked"): the state, then what follows and the next act. */
  overviewText: 'New meetings are not being imported. Syncing again will not help; connect again with a new key on Connections.',
} as const;

/** The Fellow State row's pill: the most recent run's, and whether it reads Revoked. */
export interface FellowStateRowPresentation extends LastRunPresentation {
  revoked: boolean;
}

/**
 * The Connections page's State row for Fellow (06-modules §6.4 State, "of the most recent run"): the most recent run's own
 * word (`lastRunStatePresentation`) — save a run that ended because Fellow refused the stored key, which reads Revoked, as
 * a warning, with reconnecting as the next act. Why (the owner's ruling of 2026-10-09, "Both say Revoked", on the Phase 2a
 * builder's question): the Credential row and the banner said Revoked while this row said Failed of the same run, and
 * Failed invites the retry 05-content §6 says never helps ("the reader's next act is to reconnect, not to retry"). A run
 * still running, or one queued (Pending), keeps its own word: a sync pressed after reconnecting is not refused yet.
 */
export function fellowStateRowPresentation(runs: Pick<ConnectionRunsSummary, 'last'>, syncInProgress: boolean): FellowStateRowPresentation | null {
  const last = lastRunStatePresentation(runs, syncInProgress);
  if (last === null) return null;
  const refused = last.run !== null && last.run.state === 'failed' && last.run.reason?.code === FELLOW_KEY_REFUSED;
  if (!refused) return { ...last, revoked: false };
  return { ...last, word: FELLOW_REVOKED.word, tone: 'warning', icon: 'unplug', detail: FELLOW_REVOKED.detail, revoked: true };
}

/** The account menu's Fellow line (03-components §15.2 region 2): a glyph, the words and how loudly. */
export interface FellowAccountLine {
  icon: IconName;
  text: string;
  tone: 'muted' | 'ok' | 'warn';
}

/**
 * The account menu's Fellow line for a GET /api/connections/fellow answer. Why "Revoked — reconnect" and never
 * "disconnected" (05-content §6: Disconnected is on Revoked's Not-list; the Phase 1c audit's R12): a key Fellow refused,
 * and a `revoked` connection, are one state whose next act is to connect again; any other failing connection keeps
 * "connection failing".
 */
export function fellowAccountLine(response: FellowConnectionResponse | null, failed: boolean, loading: boolean): FellowAccountLine {
  if (failed) return { icon: 'plug', text: 'Fellow: status unavailable', tone: 'muted' };
  // Why: with no failure, a missing response is only the frame before the fetch starts — loading, not a state of its own.
  if (loading || !response) return { icon: 'plug', text: 'Fellow: checking…', tone: 'muted' };
  if (response.floor === 'not_configured') return { icon: 'unplug', text: 'Fellow: not configured here', tone: 'muted' };
  if (!response.connection) return { icon: 'unplug', text: 'Fellow: not connected', tone: 'muted' };
  if (fellowCredentialRevoked(response.connection)) return { icon: 'unplug', text: FELLOW_REVOKED.accountLine, tone: 'warn' };
  if (response.connection.status === 'error') return { icon: 'alert-triangle', text: 'Fellow: connection failing', tone: 'warn' };
  const workspace = response.connection.workspaceName ?? response.connection.workspaceSubdomain;
  return { icon: 'check', text: `Fellow: connected to ${workspace}`, tone: 'ok' };
}

export interface OverviewNotice {
  kind: 'not-configured' | 'not-permitted' | 'not-connected' | 'error' | 'revoked';
  tone: 'info' | 'warning';
  title: string;
  text: string;
  /** Label for the link to the Connections page; null where there is nothing the reader can do there. */
  actionLabel: string | null;
}

/**
 * The banner the overview shows for a connection that is not quietly working. `null` when it is
 * connected — the overview then states the last sync instead of raising a notice. `errorCode` is the
 * failing connection's closed code (`OverviewResponse.connectionErrorCode`).
 */
export function overviewNotice(state: ConnectionState, me: Pick<MeResponse, 'capabilities' | 'integrations'>, errorCode: string | null = null): OverviewNotice | null {
  switch (state) {
    case 'unavailable':
      return {
        kind: 'not-configured',
        tone: 'info',
        title: 'Fellow is not switched on in this installation',
        text: 'Meetings cannot be imported until an operator configures the Fellow integration. Nothing here is failing.',
        actionLabel: null,
      };
    case 'error':
      // Why Revoked for a key Fellow refused (the owner's ruling of 2026-10-09, "Both say Revoked"; 05-content §6: "the
      // reader's next act is to reconnect, not to retry"): Connections, its banner and the account menu say Revoked, and the
      // Overview said "The last sync failed" for the same state — a retry's words for a condition no retry mends.
      if (errorCode === FELLOW_KEY_REFUSED) {
        return { kind: 'revoked', tone: 'warning', title: FELLOW_REVOKED.bannerTitle, text: FELLOW_REVOKED.overviewText, actionLabel: 'Open Connections' };
      }
      return {
        kind: 'error',
        tone: 'warning',
        title: 'Your Fellow connection needs attention',
        text: 'The last sync failed, so new meetings are not being imported. Connections shows why.',
        actionLabel: 'Open Connections',
      };
    case 'not_connected':
      if (!me.capabilities.fellowConnect && me.integrations.fellow !== 'off') {
        return {
          kind: 'not-permitted',
          tone: 'info',
          title: 'Your account cannot connect Fellow yet',
          text: 'Connecting Fellow needs the Connect Fellow capability. Ask an administrator to add you to a group that includes it.',
          actionLabel: null,
        };
      }
      return {
        kind: 'not-connected',
        tone: 'info',
        title: 'Connect your Fellow account',
        text: 'Once connected, your meeting recordings and transcripts import automatically and Calis turns them into summaries and to-dos.',
        actionLabel: 'Connect Fellow',
      };
    case 'connected':
    default:
      return null;
  }
}

// --- Ticking off in Fellow (owner, 2026-09-30; design §9.6) ---------------------------------------------

/**
 * Which of a person's Fellow items Calis ticks off there (owner decision O5(a)), said wherever ticking off is described
 * to them: the Fellow panel and Settings → Your data.
 */
export const FELLOW_TICK_OFF_ITEMS =
  'Only items Fellow assigns to you or to no one — never ones each person completes on their own, nor archived ones.';

/**
 * The panel's banner at today's limit. Why "about your Fellow action items": the guard counts only the tick-off run's
 * requests (`countItemRequest`); syncs go on past it, so "requests to Fellow" would describe a limit that does not exist.
 */
export const TICK_OFF_DAILY_LIMIT_TEXT =
  'Calis makes at most 500 requests a day about your Fellow action items. The rest of today’s ticks go to Fellow tomorrow; nothing is lost.';

/**
 * The banner at today's limit, for whichever directions the reader has (round C review): the same 500 requests a day pay
 * for ticking off in Fellow and for reading back what changed there, so the limit pauses both. Null when it pauses
 * neither (the limit is not reached, or neither is on for the reader).
 */
export function dailyLimitText(paused: { tickOff: boolean; fellowState: boolean }): string | null {
  if (paused.tickOff && paused.fellowState) {
    return 'Calis makes at most 500 requests a day about your Fellow action items. The rest of today’s ticks go to Fellow tomorrow, and changes made in Fellow reach Calis again tomorrow; nothing is lost.';
  }
  if (paused.tickOff) return TICK_OFF_DAILY_LIMIT_TEXT;
  if (paused.fellowState) return 'Calis makes at most 500 requests a day about your Fellow action items. Changes made in Fellow reach Calis again tomorrow; nothing is lost.';
  return null;
}

/** The Fellow panel's description: it says Calis ticks Fellow off only where this server lets it. */
export function fellowPanelDescription(tickOff: Pick<FellowConnectionResponse['tickOff'], 'floor'>): string {
  return tickOff.floor === 'on'
    ? 'Calis imports your meeting recordings, transcripts and action items from Fellow, and ticks off in Fellow the action items you complete here.'
    : 'Calis imports your meeting recordings and transcripts from Fellow.';
}

/**
 * The connected panel's description: it says Calis ticks Fellow off, and brings back what is changed there, only where
 * this server and the reader's account let it (the description never promises what the switch or the grant withholds).
 */
export function fellowConnectedDescription(response: Pick<FellowConnectionResponse, 'tickOff' | 'fellowState' | 'syncEveryMinutes'>): string {
  // Why this server's own interval (round C review): the "Changes made in Fellow" field beside it says it, and "every few
  // minutes" next to "every hour" contradicted it.
  const every = everyMinutesText(response.syncEveryMinutes);
  // Why four whole strings (05-content §8): a sentence is never assembled from clauses spliced in by condition — word
  // order differs between languages — so each combination of what the reader is allowed is written out in full, with
  // the interval its one placeholder.
  if (response.tickOff.allowed && response.fellowState.allowed) {
    return `Calis polls Fellow ${every} for new recordings, and ticks off in Fellow the action items you complete here. What you tick off or reopen in Fellow reaches Calis too.`;
  }
  if (response.tickOff.allowed) return `Calis polls Fellow ${every} for new recordings, and ticks off in Fellow the action items you complete here.`;
  if (response.fellowState.allowed) return `Calis polls Fellow ${every} for new recordings. What you tick off or reopen in Fellow reaches Calis too.`;
  return `Calis polls Fellow ${every} for new recordings.`;
}

export interface TickOffPresentation {
  kind: 'off' | 'not-allowed' | 'daily-limit' | 'paused' | 'on';
  /** The panel's "Ticking off in Fellow" field, in a few words. */
  word: string;
  detail: string;
}

/**
 * Ticking off in Fellow as the connected panel states it, its states kept apart (03-components §1): inactive on this
 * server (the deployment's switch, which binds everyone — nothing is failing), not available to you (the switch is
 * on but the account lacks Connect Fellow), today's limit reached, awaiting the connection, or active.
 *
 * Why these words (conformity 2026-10-05, R2; owner's pick C): the state words are 05-content §6's — Active and
 * Inactive, whose Not-lists hold On, Off and Paused — the limit's Inactive carrying its reason beside it, and a wait
 * on the connection is "Awaiting" plus what is awaited, one complete string (§6, 8.3.0). The `kind`s are unchanged.
 */
export function tickOffPresentation(response: Pick<FellowConnectionResponse, 'tickOff' | 'connection'>): TickOffPresentation {
  const { tickOff, connection } = response;
  if (tickOff.floor === 'off') {
    return { kind: 'off', word: 'Inactive on this server', detail: 'An operator can switch it on; nothing is failing.' };
  }
  if (!tickOff.allowed) {
    return { kind: 'not-allowed', word: 'Not available to you', detail: "It comes with the Connect Fellow capability, which your account doesn't include. Ask an administrator if you need it." };
  }
  if (connection !== null && connection.status !== 'active') {
    return { kind: 'paused', word: 'Awaiting the connection', detail: 'Ticks wait until your Fellow connection works again, then go to Fellow.' };
  }
  if (tickOff.dailyLimitReached) {
    return { kind: 'daily-limit', word: 'Inactive — today’s limit reached', detail: 'Today’s limit of Fellow requests is reached; the rest are ticked off in Fellow tomorrow.' };
  }
  return {
    kind: 'on',
    word: 'Active',
    detail: `Action items you complete here are ticked off in Fellow too, with your key; a colleague’s tick that reaches your linked copy is too. ${FELLOW_TICK_OFF_ITEMS}`,
  };
}

// --- Changes made in Fellow (Stage 3, "Fellow → Calis"; owner decision O4(a)) ------------------------------

/** How often something happens, in minutes, as a person reads it: "every 15 minutes", "every hour", "every 2 days". */
export function everyMinutesText(minutes: number): string {
  const whole = Math.max(1, Math.round(minutes));
  if (whole % 1440 === 0) return pluralise(whole / 1440, { one: 'every day', other: 'every # days' });
  if (whole % 60 === 0) return pluralise(whole / 60, { one: 'every hour', other: 'every # hours' });
  return pluralise(whole, { one: 'every minute', other: 'every # minutes' });
}

/**
 * How soon a change made in Fellow reaches Calis (O4(a)), said wherever the read is described: the panel (with this
 * server's sync interval) and Settings (without it: /api/me does not carry it). Why not a fixed "about 15 minutes" (round C
 * review): the read follows each successful sync, whose interval the operator sets. Why "reach Calis" and not "come back
 * to your to-do": some are only offered (the next sentence where it is used says when).
 */
export function fellowStateTiming(syncEveryMinutes: number | null): string {
  // Why "made in Fellow", not "you make": a change anyone makes there to an item Calis holds for the reader comes back
  // (SECURITY.md). Why the 30 days: the daily pass that sees a reopen made on Fellow's list reads only the items created
  // in the last 30 days (and at most the newest 1,000), so "within a day" alone promised more than it does. Why two whole
  // sentences (05-content §8; the Phase 1c audit's D30): the interval's parenthesis was spliced in by condition.
  if (syncEveryMinutes === null) return 'Ticks and reopens made in Fellow reach Calis after each sync with Fellow; a reopen made on Fellow’s action-items page, within a day, for items created in the last 30 days.';
  return `Ticks and reopens made in Fellow reach Calis after each sync with Fellow (${everyMinutesText(syncEveryMinutes)} on this server); a reopen made on Fellow’s action-items page, within a day, for items created in the last 30 days.`;
}

/** What Calis does with a change that reached it, said beside the timing: applied, or offered when it can't tell. */
export const FELLOW_STATE_OUTCOME =
  'Calis ticks off or reopens your own to-do to match, or offers to when you have changed it since or it can’t tell it should, and never sends that change back to Fellow.';

/** What the read keeps (design §7.2): only the items Calis already holds for the person; the rest are dropped unread. */
export const FELLOW_STATE_PRIVACY =
  'To see them, Calis reads your Fellow action-item list with your own API key, and keeps nothing of items it doesn’t already hold for you.';

/**
 * What the person can do while the last read did not get through, by its cause. Why no "Connect again" here: none of
 * these is the key's sign-in — a key Fellow no longer accepts breaks the connection itself, and the connection's own
 * banner and "Connect again" say so. In every case the imports carry on (the read is isolated, §7.2),
 * and a tick made in Fellow can still be made here.
 */
const FELLOW_STATE_REMEDY: Readonly<Record<FellowStateReadCode, string>> = {
  forbidden: 'Your imports carry on. Check in Fellow that your API key may read your action items; Calis tries again at every sync.',
  not_offered: 'Your imports carry on, and Calis tries again at every sync. If this stays, ask your Fellow workspace administrator whether action items are available to API keys.',
  refused: 'Your imports carry on, and Calis tries again at every sync. If this stays, tell your Calis administrator.',
  unavailable: 'Your imports carry on.',
};

export interface FellowStatePresentation {
  kind: 'off' | 'not-allowed' | 'paused' | 'daily-limit' | 'error' | 'on';
  /** The panel's "Changes made in Fellow" field, in a few words. */
  word: string;
  detail: string;
  /** The warning banner while the last read did not get through (03-components §6.2): what failed and what to do. */
  banner: { title: string; text: string; remedy: string } | null;
}

/**
 * Changes made in Fellow coming back to Calis, as the connected panel states it, its states kept apart (03-components
 * §1): inactive on this server (the deployment's switch, which binds everyone — nothing is failing), not available to
 * you (the switch is on but the account lacks Connect Fellow), awaiting the connection, today's limit of Fellow requests
 * reached (the read waits for tomorrow: round C review — the active word said what was not happening), the last read's
 * failure, or active. The words are 05-content §6's, as `tickOffPresentation` says (R2).
 */
export function fellowStatePresentation(response: Pick<FellowConnectionResponse, 'fellowState' | 'connection' | 'syncEveryMinutes' | 'tickOff'>): FellowStatePresentation {
  const { fellowState, connection } = response;
  if (fellowState.floor === 'off') {
    return { kind: 'off', word: 'Inactive on this server', detail: 'An operator can switch it on; nothing is failing.', banner: null };
  }
  if (!fellowState.allowed) {
    return { kind: 'not-allowed', word: 'Not available to you', detail: "It comes with the Connect Fellow capability, which your account doesn't include. Ask an administrator if you need it.", banner: null };
  }
  if (connection !== null && connection.status !== 'active') {
    return { kind: 'paused', word: 'Awaiting the connection', detail: 'Changes made in Fellow come back once your Fellow connection works again.', banner: null };
  }
  if (response.tickOff.dailyLimitReached) {
    return { kind: 'daily-limit', word: 'Inactive — today’s limit reached', detail: 'Today’s limit of Fellow requests is reached; changes made in Fellow reach Calis again tomorrow.', banner: null };
  }
  if (fellowState.error !== null) {
    const text = FELLOW_STATE_READ_CODE_LABELS[fellowState.error];
    return {
      kind: 'error',
      word: 'Not reaching Calis',
      detail: text,
      banner: { title: 'Changes made in Fellow aren’t reaching Calis', text, remedy: `${FELLOW_STATE_REMEDY[fellowState.error]} Until it works, tick off here what you tick off in Fellow.` },
    };
  }
  // Why the day of the last daily pass: it is the one that catches reopens made on Fellow's action-items page (U4). Why
  // "daily" and not "full" (round C review): the pass reads the items created in the last 30 days, at most 1,000.
  const daily = fellowState.dailyOn ? ` Last daily check ${formatDateOnly(fellowState.dailyOn)}.` : '';
  return { kind: 'on', word: 'Active', detail: `${fellowStateTiming(response.syncEveryMinutes)} ${FELLOW_STATE_OUTCOME} ${FELLOW_STATE_PRIVACY}${daily}`, banner: null };
}

/** The panel's line about failed writes: "2 to-dos couldn't be ticked off in Fellow". Null when none failed. */
export function tickOffFailingTitle(failing: number): string | null {
  if (failing <= 0) return null;
  return pluralise(failing, { one: "# to-do couldn't be ticked off in Fellow", other: "# to-dos couldn't be ticked off in Fellow" });
}

/**
 * Where the panel's Review leads: the To-dos page filtered to "Not ticked off in Fellow", at every status — most
 * failures are on done to-dos, and the list opens on the open ones.
 */
export const TICK_OFF_REVIEW_PATH = '/todos?fellowWrite=failed&status=all';
